import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axiosInstance from "../../../Fbaseurl";
import { showToast } from "../../../store/reducers/ToastMessageReducer";
import { useDispatch, useSelector } from "react-redux";
import { useMediaQuery } from "@mui/material";
import "./index.css";
import AutocompleteResult from "../../components/autocomplete-result";
import { AutocompleteCustom } from "../../components/autocomplete-custom";
import { clearFindBusinessResults } from "../../../store/actions/resultsCacheActions";
import {
  APIProvider,
  Map as GoogleMap
} from "@vis.gl/react-google-maps";
import {
  EMPOWERMENT_FILTER_VALUES,
  EMPOWERMENT_ZONE_TYPES,
  getEmpowermentZoneCategory,
} from "../../../CommonUtils/empowermentZones";
import {
  getRoleBasePath,
  isSuperAdminRole,
} from "../../../CommonUtils/roles";

const GOOGLE_API_KEY = process.env.REACT_APP_MAPS_API_KEY;

const US_STATES = [
  { code: "AL", name: "Alabama" }, { code: "AK", name: "Alaska" },
  { code: "AZ", name: "Arizona" }, { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" }, { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" }, { code: "DE", name: "Delaware" },
  { code: "FL", name: "Florida" }, { code: "GA", name: "Georgia" },
  { code: "HI", name: "Hawaii" }, { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" }, { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" }, { code: "KS", name: "Kansas" },
  { code: "KY", name: "Kentucky" }, { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" }, { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" }, { code: "MI", name: "Michigan" },
  { code: "MN", name: "Minnesota" }, { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" }, { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" }, { code: "NV", name: "Nevada" },
  { code: "NH", name: "New Hampshire" }, { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" }, { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" }, { code: "ND", name: "North Dakota" },
  { code: "OH", name: "Ohio" }, { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" }, { code: "PA", name: "Pennsylvania" },
  { code: "RI", name: "Rhode Island" }, { code: "SC", name: "South Carolina" },
  { code: "SD", name: "South Dakota" }, { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" }, { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" }, { code: "VA", name: "Virginia" },
  { code: "WA", name: "Washington" }, { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" }, { code: "WY", name: "Wyoming" },
  { code: "DC", name: "District of Columbia" },
];

const DEFAULT_US_CENTER = { lat: 37.0902, lng: -95.7129 };

function getStateCode(stateName) {
  if (!stateName) return "";
  const st = US_STATES.find(
    (s) =>
      s.name.toLowerCase() === stateName.toLowerCase() ||
      s.code.toLowerCase() === stateName.toLowerCase()
  );
  return st ? st.code : "";
}

const mapZoneSelectionToFilters = (selection = {}) => {
  const filters = [];
  if (selection?.fez) filters.push(EMPOWERMENT_FILTER_VALUES.EMPOWERMENT);
  if (selection?.rrc) filters.push(EMPOWERMENT_FILTER_VALUES.RURAL);
  return filters;
};

const BUSINESS_TYPE_SEARCH_STOP_WORDS = new Set(["and", "&"]);
const getBusinessTypeSearchTokens = (value = "") =>
  value
    .split(/[^0-9a-z]+/i)
    .map((token) => token.trim().toLowerCase())
    .filter(
      (token) =>
        token &&
        token.length > 1 &&
        !BUSINESS_TYPE_SEARCH_STOP_WORDS.has(token)
    );

const ZONE_KEY_TO_TYPE = {
  fez: EMPOWERMENT_ZONE_TYPES.EMPOWERMENT,
  rrc: EMPOWERMENT_ZONE_TYPES.RURAL,
};

const buildSearchRequestConfig = (params = {}) => {
  const serializer = (values = {}) => {
    const search = new URLSearchParams();
    Object.entries(values).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((entry) => {
          if (entry !== undefined && entry !== null && entry !== "") {
            search.append(key, entry);
          }
        });
      } else if (
        value !== undefined &&
        value !== null &&
        value !== ""
      ) {
        search.append(key, value);
      }
    });
    return search.toString();
  };
  return {
    params,
    paramsSerializer: serializer,
  };
};

const parseCodeInput = (value = "") =>
  value
    .split(/[\s,]+/)
    .map((code) => code.trim())
    .filter(Boolean);

function parseGooglePlace(place) {
  const components = place.address_components || [];
  let city = "",
    state = "",
    zip = "",
    streetAddress = "";
  components.forEach((comp) => {
    if (comp.types.includes("locality")) city = comp.long_name;
    if (comp.types.includes("administrative_area_level_1"))
      state = comp.short_name;
    if (comp.types.includes("postal_code")) zip = comp.long_name;
    if (comp.types.includes("street_number"))
      streetAddress =
        comp.long_name + (streetAddress ? " " + streetAddress : "");
    if (comp.types.includes("route"))
      streetAddress =
        (streetAddress ? streetAddress + " " : "") + comp.long_name;
  });
  return { city, state, zip, streetAddress };
}

function ReviewDetails() {
  const isMobile = useMediaQuery("(max-width:900px)");
  const dispatch = useDispatch();
  const currentRole = useSelector((state) => state.App.currentRole);
  const roleBasePath = getRoleBasePath(currentRole);
  const rolePageSize = isSuperAdminRole(currentRole) ? 50 : 20;
  let navigate = useNavigate();
  const location = useLocation();

  const LOCATION_MODE = "location";
  const MANUAL_ENTRY_MODE = "manualEntry";
  const MANUAL_CALC_MODE = "manualCalc";

  const initialMode = location.state && location.state.openManual ? MANUAL_CALC_MODE : LOCATION_MODE;

  const [mode, setMode] = useState(initialMode);
  useEffect(() => {
    if (mode !== LOCATION_MODE && isMobile && typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [mode, isMobile]);
  const [form, setForm] = useState({
    businessName: "",
    streetAddress: "",
    city: "",
    state: "",
    zip: "",
    minEmployeeCount: "",
    maxEmployeeCount: "",
    businessType: "",
    autocompleteValue: "",
    annualHires: "", // <-- ADD annualHires field for user input
    naicsInput: "",
    naicsCodes: [],
    censusInput: "",
    censusCodes: [],
  });
  const [errors, setErrors] = useState({
    stateOrLocation: false,
    locationRequired: false,
    naicsCodes: "",
    censusCodes: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [mapCenter, setMapCenter] = useState(DEFAULT_US_CENTER);
  const [mapZoom, setMapZoom] = useState(4);
  const [marker, setMarker] = useState(null);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [censusZoneSelection, setCensusZoneSelection] = useState({
    fez: false,
    rrc: false,
  });
  const [empowermentZoneCache, setEmpowermentZoneCache] = useState({});
  const [isZoneLoading, setIsZoneLoading] = useState(false);
  const [zoneError, setZoneError] = useState("");
  const [zoneCensusOptions, setZoneCensusOptions] = useState([]);
  const [selectedZoneTracts, setSelectedZoneTracts] = useState([]);
  const [isCensusDropdownOpen, setIsCensusDropdownOpen] = useState(false);
  const zoneSelectionActiveRef = useRef(false);
  const manualCensusBackupRef = useRef("");
  const isStateReady = (form.state || "").trim().length === 2;
  const isZoneSelectionActive =
    censusZoneSelection.fez || censusZoneSelection.rrc;
  const selectedZoneTractSet = useMemo(
    () => new Set(selectedZoneTracts),
    [selectedZoneTracts]
  );
  const [naicsOptions, setNaicsOptions] = useState([]);
  const [isBusinessTypeDropdownOpen, setIsBusinessTypeDropdownOpen] = useState(false);
  const [isNaicsLoading, setIsNaicsLoading] = useState(false);
  const businessTypeDropdownRef = useRef(null);
  const hasFetchedNaicsRef = useRef(false);
  const [isNaicsCodesDropdownOpen, setIsNaicsCodesDropdownOpen] = useState(false);
  const [naicsCodeSearchTerm, setNaicsCodeSearchTerm] = useState("");
  const naicsCodesDropdownRef = useRef(null);
  const [stateCensusOptions, setStateCensusOptions] = useState([]);
  const [isManualCensusDropdownOpen, setIsManualCensusDropdownOpen] =
    useState(false);
  const manualCensusDropdownRef = useRef(null);
  const [manualCensusSearchTerm, setManualCensusSearchTerm] = useState("");
  const autoSelectZoneTypesRef = useRef(new Set());
  const suppressZoneResetRef = useRef(false);
  const hasValidSearchTerm = (value = "") =>
    value.replace(/[^0-9a-z]/gi, "").length >= 2;

  const selectAllZoneTracts = useCallback(
    (zoneType, preserveExisting = false) => {
      if (!zoneType) return false;
      const codes = stateCensusOptions
        .filter((option) => option.zoneType === zoneType)
        .map((option) => option.code)
        .filter(Boolean);
      if (!codes.length) return false;
      suppressZoneResetRef.current = true;
      setSelectedZoneTracts((prev) => {
        const nextSet = new Set(
          preserveExisting ? prev : []
        );
        codes.forEach((code) => nextSet.add(code));
        return Array.from(nextSet);
      });
      return true;
    },
    [stateCensusOptions]
  );

  const toggleZoneSelection = (key) => {
    if (!isStateReady) return;
    setZoneError("");
    setCensusZoneSelection((prev) => {
      const nextValue = !prev[key];
      const updated = { ...prev, [key]: nextValue };
      const zoneType = ZONE_KEY_TO_TYPE[key];
      if (zoneType) {
        if (nextValue) {
          const preserveExisting =
            (zoneType === EMPOWERMENT_ZONE_TYPES.EMPOWERMENT && prev.rrc) ||
            (zoneType === EMPOWERMENT_ZONE_TYPES.RURAL && prev.fez);
          const applied = selectAllZoneTracts(zoneType, preserveExisting);
          if (!applied) {
            autoSelectZoneTypesRef.current.add(zoneType);
          } else {
            autoSelectZoneTypesRef.current.delete(zoneType);
          }
        } else {
          autoSelectZoneTypesRef.current.delete(zoneType);
        }
      }
      return updated;
    });
  };

  const fetchNaicsOptions = useCallback(async () => {
    if (isNaicsLoading || hasFetchedNaicsRef.current) return;
    setIsNaicsLoading(true);
    try {
      const response = await axiosInstance.get("/businessSearch/GetNaicsCodes");
      const rawData = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.data?.data)
        ? response.data.data
        : [];
      const normalized = rawData.filter(
        (item) => item && (item.naicsCode || item.naicsDescription)
      );
      setNaicsOptions(normalized);
      if (normalized.length) {
        hasFetchedNaicsRef.current = true;
      }
    } catch (error) {
      dispatch(
        showToast({
          type: "error",
          message: "Unable to load NAICS codes. Please try again.",
        })
      );
    } finally {
      setIsNaicsLoading(false);
    }
  }, [dispatch, isNaicsLoading]);

  const filteredNaicsOptions = useMemo(() => {
    if (!naicsOptions.length) return [];
    const query = (form.businessType || "").trim().toLowerCase();
    if (!query) {
      return naicsOptions.slice(0, 25);
    }
    const tokens = getBusinessTypeSearchTokens(query);
    if (!tokens.length) {
      return naicsOptions.slice(0, 25);
    }
    return naicsOptions
      .filter((item) => {
        const code = (item.naicsCode || "").toLowerCase();
        const desc = (item.naicsDescription || "").toLowerCase();
        return tokens.some(
          (token) => code.includes(token) || desc.includes(token)
        );
      })
      .slice(0, 25);
  }, [form.businessType, naicsOptions]);

  const ensureNaicsOptions = (options = {}) => {
    const { requiresSearchSeed = false, searchSeed = "" } = options;
    if (
      requiresSearchSeed &&
      !hasFetchedNaicsRef.current &&
      !hasValidSearchTerm(searchSeed)
    ) {
      return false;
    }
    if (!hasFetchedNaicsRef.current && !isNaicsLoading) {
      fetchNaicsOptions();
      return true;
    }
    return hasFetchedNaicsRef.current;
  };

  const handleBusinessTypeInputChange = (value) => {
    setForm((f) => ({ ...f, businessType: value }));
    if (!isBusinessTypeDropdownOpen) {
      setIsBusinessTypeDropdownOpen(true);
    }
    ensureNaicsOptions();
  };

  const handleBusinessTypeFocus = () => {
    setIsBusinessTypeDropdownOpen(true);
    ensureNaicsOptions();
  };

  const toggleBusinessTypeDropdown = () => {
    setIsBusinessTypeDropdownOpen((open) => {
      const next = !open;
      if (next) {
        ensureNaicsOptions();
      }
      return next;
    });
  };

  const handleBusinessTypeOptionSelect = (option) => {
    if (!option) return;
    const formatted =
      option.naicsDescription && option.naicsCode
        ? `${option.naicsDescription} (${option.naicsCode})`
        : option.naicsDescription || option.naicsCode || "";
    setForm((f) => ({ ...f, businessType: formatted }));
    setIsBusinessTypeDropdownOpen(false);
  };

  const filteredNaicsCodeOptions = useMemo(() => {
    if (!naicsOptions.length) return [];
    const query = naicsCodeSearchTerm.trim().toLowerCase();
    if (!query) {
      return naicsOptions.slice(0, 25);
    }
    return naicsOptions
      .filter((item) => {
        const code = (item.naicsCode || "").toLowerCase();
        const desc = (item.naicsDescription || "").toLowerCase();
        return code.includes(query) || desc.includes(query);
      })
      .slice(0, 25);
  }, [naicsCodeSearchTerm, naicsOptions]);

  const handleNaicsCodesFocus = () => {
    setIsNaicsCodesDropdownOpen(true);
    ensureNaicsOptions({
      requiresSearchSeed: true,
      searchSeed: naicsCodeSearchTerm,
    });
  };

  const handleNaicsCodesChange = (value) => {
    const parts = value.split(/[\s,]+/);
    const currentTerm = parts[parts.length - 1] || "";
    setNaicsCodeSearchTerm(currentTerm);
    handleCodeInputChange(
      "naicsInput",
      "naicsCodes",
      value,
      { minLength: 2, maxLength: 6, label: "NAICS code" }
    );
    setIsNaicsCodesDropdownOpen(true);
    ensureNaicsOptions({
      requiresSearchSeed: true,
      searchSeed: currentTerm,
    });
  };

  const handleNaicsCodeOptionSelect = (option) => {
    const code = (option?.naicsCode || "").trim();
    if (!code) return;
    const trimmedSearch = naicsCodeSearchTerm.trim().toLowerCase();
    let segments = (form.naicsInput || "")
      .split(",")
      .map((segment) => segment.trim())
      .filter(Boolean);

    if (
      trimmedSearch &&
      segments.length &&
      segments[segments.length - 1].toLowerCase() === trimmedSearch
    ) {
      segments.pop();
    }

    if (!segments.includes(code)) {
      segments.push(code);
    }

    const newValue = segments.join(", ");
    handleCodeInputChange(
      "naicsInput",
      "naicsCodes",
      newValue,
      { minLength: 2, maxLength: 6, label: "NAICS code" }
    );
    setNaicsCodeSearchTerm("");
    setIsNaicsCodesDropdownOpen(false);
  };

  const handleManualCensusFocus = () => {
    if (isZoneSelectionActive) return;
    if (
      manualCensusSearchTerm.trim().length >= 2 &&
      stateCensusOptions.length
    ) {
      setIsManualCensusDropdownOpen(true);
    }
  };

  const handleManualCensusTextChange = (value) => {
    handleCodeInputChange(
      "censusInput",
      "censusCodes",
      value,
      { minLength: 6, maxLength: 6, label: "census code" }
    );
    if (isZoneSelectionActive) return;
    const parts = value.split(/[\s,]+/);
    const currentTerm = parts[parts.length - 1] || "";
    setManualCensusSearchTerm(currentTerm);
    if (
      currentTerm.trim().length >= 2 &&
      stateCensusOptions.length > 0
    ) {
      setIsManualCensusDropdownOpen(true);
    } else {
      setIsManualCensusDropdownOpen(false);
    }
  };

  const handleManualCensusOptionSelect = (option) => {
    const code = option?.code;
    if (!code) return;
    let segments = (form.censusInput || "")
      .split(",")
      .map((segment) => segment.trim())
      .filter(Boolean);

    const trimmedSearch = manualCensusSearchTerm.trim().toLowerCase();
    if (
      trimmedSearch &&
      segments.length &&
      segments[segments.length - 1].toLowerCase() === trimmedSearch
    ) {
      segments.pop();
    }

    if (!segments.includes(code)) {
      segments.push(code);
    }

    const newValue = segments.join(", ");
    handleCodeInputChange(
      "censusInput",
      "censusCodes",
      newValue,
      { minLength: 6, maxLength: 6, label: "census code" }
    );
    setManualCensusSearchTerm("");
    setIsManualCensusDropdownOpen(false);
  };

  const manualCensusSuggestions = useMemo(() => {
    if (isZoneSelectionActive) return [];
    const term = (manualCensusSearchTerm || "").trim().toLowerCase();
    if (term.length < 2) return [];
    return stateCensusOptions
      .filter(
        (option) =>
          option.code.toLowerCase().includes(term) ||
          (option.name || "").toLowerCase().includes(term)
      )
      .slice(0, 25);
  }, [manualCensusSearchTerm, stateCensusOptions, isZoneSelectionActive]);

  useEffect(() => {
    const handleClickAway = (event) => {
      if (
        businessTypeDropdownRef.current &&
        !businessTypeDropdownRef.current.contains(event.target)
      ) {
        setIsBusinessTypeDropdownOpen(false);
      }
      if (
        naicsCodesDropdownRef.current &&
        !naicsCodesDropdownRef.current.contains(event.target)
      ) {
        setIsNaicsCodesDropdownOpen(false);
      }
      if (
        manualCensusDropdownRef.current &&
        !manualCensusDropdownRef.current.contains(event.target)
      ) {
        setIsManualCensusDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickAway);
    return () => document.removeEventListener("mousedown", handleClickAway);
  }, []);

  const buildCensusTractOptions = (zones = [], selection = {}) => {
    if (!Array.isArray(zones)) return [];
    const allowed = [];
    if (selection.fez) allowed.push(EMPOWERMENT_ZONE_TYPES.EMPOWERMENT);
    if (selection.rrc) allowed.push(EMPOWERMENT_ZONE_TYPES.RURAL);
    if (!allowed.length) return [];

    const seen = new Set();
    const options = [];
    zones.forEach((item) => {
      const category = getEmpowermentZoneCategory(item.zone);
      if (!allowed.includes(category)) return;
      const tract = String(item.censusTract || "").trim();
      if (!tract || seen.has(tract)) return;

      const zoneLabel = String(item.zoneName || item.zone || "").trim();
      const county = String(item.countyName || item.county || "").trim();
      const city = String(item.city || item.cityName || "").trim();
      const state = String(item.state || item.stateCode || "").trim();
      const descriptorParts = [];
      if (county) descriptorParts.push(county);
      if (state) descriptorParts.push(state);
      if (!descriptorParts.length && city) descriptorParts.push(city);

      seen.add(tract);
      const friendlyName =
        String(item.name || "").trim() ||
        descriptorParts.join(", ");

      options.push({
        code: tract,
        zoneType: category,
        zoneLabel,
        name: friendlyName,
        meta: descriptorParts.join(", "),
      });
    });
    return options;
  };

  const applyCensusCodesFromZones = (codes = []) => {
    const uniqueCodes = Array.from(
      new Set(
        (codes || [])
          .map((code) => String(code || "").trim())
          .filter(Boolean)
      )
    );
    setForm((prev) => ({
      ...prev,
      censusInput: uniqueCodes.join(", "),
      censusCodes: uniqueCodes,
    }));
  };

  const toggleCensusOptionSelection = (code) => {
    if (!code) return;
    setSelectedZoneTracts((prev) => {
      if (prev.includes(code)) {
        return prev.filter((entry) => entry !== code);
      }
      return [...prev, code];
    });
  };

  const selectAllCensusOptions = () => {
    if (!zoneCensusOptions.length) return;
    const allCodes = zoneCensusOptions.map((option) => option.code);
    setSelectedZoneTracts((prev) => {
      if (
        prev.length === allCodes.length &&
        prev.every((code, index) => code === allCodes[index])
      ) {
        return prev;
      }
      return allCodes;
    });
  };

  const clearSelectedCensusOptions = () => {
    setSelectedZoneTracts((prev) => (prev.length ? [] : prev));
  };
  const mapKey = `${mapCenter.lat}-${mapCenter.lng}`;
  const manualCalcReady = useMemo(() => {
    if (mode !== MANUAL_CALC_MODE) return true;
    const requiredTextFields = ["businessName", "streetAddress", "city", "zip", "businessType"];
    for (const field of requiredTextFields) {
      if (!String(form[field] || "").trim()) {
        return false;
      }
    }
    if (!form.state || form.state.length !== 2) return false;
    const numericFields = ["annualHires"];
    for (const field of numericFields) {
      const value = form[field];
      if (value === "" || value === null || typeof value === "undefined") {
        return false;
      }
      const parsed = Number(value);
      if (Number.isNaN(parsed) || parsed < 0) {
        return false;
      }
    }
    return true;
  }, [form, mode]);

  // If prefill provided from Blog (when user clicks calculate manually from results page), apply it
  useEffect(() => {
    if (location.state && location.state.prefill) {
      const pf = location.state.prefill || {};
      setForm((f) => ({
        ...f,
        businessName: pf.businessName || f.businessName,
        streetAddress: pf.streetAddress || f.streetAddress,
        city: pf.city || f.city,
        state: pf.state || f.state,
        zip: pf.zip || f.zip,
        minEmployeeCount: pf.minEmployeeCount || f.minEmployeeCount,
        maxEmployeeCount: pf.maxEmployeeCount || f.maxEmployeeCount,
        businessType: pf.businessType || f.businessType,
        autocompleteValue: pf.autocompleteValue || f.autocompleteValue,
        annualHires: pf.annualHires || "",
        naicsInput: pf.naicsInput || "",
        naicsCodes: Array.isArray(pf.naicsCodes) ? pf.naicsCodes : [],
        censusInput: pf.censusInput || "",
        censusCodes: Array.isArray(pf.censusCodes) ? pf.censusCodes : [],
      }));
      if (location.state.openManual) setMode(MANUAL_CALC_MODE);
    }
    try {
      const raw = sessionStorage.getItem("openManualFromResults");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && parsed.openManual) {
          const pf = parsed.prefill || {};
          setForm((f) => ({
            ...f,
            businessName: pf.businessName || f.businessName,
            streetAddress: pf.streetAddress || f.streetAddress,
            city: pf.city || f.city,
            state: pf.state || f.state,
            zip: pf.zip || f.zip,
            minEmployeeCount: pf.minEmployeeCount || f.minEmployeeCount,
            maxEmployeeCount: pf.maxEmployeeCount || f.maxEmployeeCount,
            businessType: pf.businessType || f.businessType,
            autocompleteValue: pf.autocompleteValue || f.autocompleteValue,
            annualHires: pf.annualHires || "",
            naicsInput: pf.naicsInput || "",
            naicsCodes: Array.isArray(pf.naicsCodes) ? pf.naicsCodes : [],
            censusInput: pf.censusInput || "",
            censusCodes: Array.isArray(pf.censusCodes) ? pf.censusCodes : [],
          }));
          setMode(MANUAL_CALC_MODE);
        }
      }
    } catch (e) {
    } finally {
      sessionStorage.removeItem("openManualFromResults");
    }
  }, [location.state]);

  useEffect(() => {
    if (!selectedPlace || !selectedPlace.geometry || !selectedPlace.geometry.location) return;
    const loc = {
      lat: selectedPlace.geometry.location.lat(),
      lng: selectedPlace.geometry.location.lng(),
    };
    setMapCenter(loc);
    setMapZoom(13);
    setMarker(loc);
    if (selectedPlace.address_components) {
      const { city, state, zip, streetAddress } = parseGooglePlace(selectedPlace);
      setForm((f) => ({
        ...f,
        city,
        state: getStateCode(state),
        zip,
        streetAddress,
        autocompleteValue: `${city}, ${state} ${zip}`,
      }));
    }
  }, [selectedPlace]);

  useEffect(() => {
    const anySelected = censusZoneSelection.fez || censusZoneSelection.rrc;
    if (anySelected && !zoneSelectionActiveRef.current) {
      manualCensusBackupRef.current = form.censusInput || "";
      if (!suppressZoneResetRef.current) {
        setSelectedZoneTracts([]);
      }
      suppressZoneResetRef.current = false;
      setZoneCensusOptions([]);
      setIsCensusDropdownOpen(false);
      setIsManualCensusDropdownOpen(false);
    }
    if (!anySelected && zoneSelectionActiveRef.current) {
      setZoneError("");
      setZoneCensusOptions([]);
      setSelectedZoneTracts([]);
      setIsCensusDropdownOpen(false);
      const restore = manualCensusBackupRef.current || "";
      setForm((prev) => ({
        ...prev,
        censusInput: restore,
        censusCodes: parseCodeInput(restore),
      }));
      setManualCensusSearchTerm("");
      setIsManualCensusDropdownOpen(false);
    }
    zoneSelectionActiveRef.current = anySelected;
    if (!anySelected) {
      autoSelectZoneTypesRef.current.clear();
    }
  }, [censusZoneSelection, form.censusInput]);

  useEffect(() => {
    if (isStateReady) return;
    if (censusZoneSelection.fez || censusZoneSelection.rrc) {
      setCensusZoneSelection({ fez: false, rrc: false });
    }
  }, [isStateReady, censusZoneSelection]);

  useEffect(() => {
    if (!stateCensusOptions.length) {
      setIsManualCensusDropdownOpen(false);
      setManualCensusSearchTerm("");
    }
  }, [stateCensusOptions]);

  useEffect(() => {
    const stateCode = (form.state || "").trim().toUpperCase();
    if (stateCode.length !== 2) {
      setStateCensusOptions([]);
      return;
    }
    const cached = empowermentZoneCache[stateCode];
    if (cached) {
      setStateCensusOptions(
        buildCensusTractOptions(cached, { fez: true, rrc: true })
      );
      return;
    }
    let isMounted = true;
    const loadStateCensusOptions = async () => {
      try {
        setZoneError("");
        setIsZoneLoading(true);
        const response = await axiosInstance.get(
          `/taxCredits/GetEmpowermentZoneByState`,
          {
            params: { State: stateCode },
          }
        );
        const zoneData = Array.isArray(response.data?.censusDetail)
          ? response.data.censusDetail
          : [];
        if (!isMounted) return;
        setEmpowermentZoneCache((prev) => ({
          ...prev,
          [stateCode]: zoneData,
        }));
        const options = buildCensusTractOptions(zoneData, {
          fez: true,
          rrc: true,
        });
        setStateCensusOptions(options);
      } catch (error) {
        if (!isMounted) return;
        setStateCensusOptions([]);
        setZoneError("Failed to fetch empowerment zone details.");
      } finally {
        if (isMounted) {
          setIsZoneLoading(false);
        }
      }
    };
    loadStateCensusOptions();
    return () => {
      isMounted = false;
    };
  }, [form.state, empowermentZoneCache]);

  useEffect(() => {
    const anySelected = censusZoneSelection.fez || censusZoneSelection.rrc;
    if (!anySelected) {
      setZoneCensusOptions([]);
      setIsCensusDropdownOpen(false);
      return;
    }
    const allowed = [];
    if (censusZoneSelection.fez) {
      allowed.push(EMPOWERMENT_ZONE_TYPES.EMPOWERMENT);
    }
    if (censusZoneSelection.rrc) {
      allowed.push(EMPOWERMENT_ZONE_TYPES.RURAL);
    }
    if (!allowed.length) {
      setZoneCensusOptions([]);
      setIsCensusDropdownOpen(false);
      return;
    }
    const filtered = stateCensusOptions.filter((option) =>
      allowed.includes(option.zoneType)
    );
    setZoneCensusOptions(filtered);
    setIsCensusDropdownOpen(filtered.length > 0);
    if (!filtered.length) {
      setZoneError(
        "No census tracts found for the selected option(s) in this state."
      );
    } else {
      setZoneError("");
    }
    const allowedCodes = new Set(filtered.map((option) => option.code));
    const zoneTypeMap = new Map();
    filtered.forEach((option) => {
      if (!zoneTypeMap.has(option.zoneType)) {
        zoneTypeMap.set(option.zoneType, []);
      }
      zoneTypeMap.get(option.zoneType).push(option.code);
    });
    const pendingZones = autoSelectZoneTypesRef.current;
    setSelectedZoneTracts((prev) => {
      let changed = false;
      const nextSet = new Set();
      prev.forEach((code) => {
        if (allowedCodes.has(code)) {
          nextSet.add(code);
        } else {
          changed = true;
        }
      });
      const processedZones = [];
      pendingZones.forEach((zoneType) => {
        const codes = zoneTypeMap.get(zoneType);
        if (codes && codes.length) {
          codes.forEach((code) => {
            if (!nextSet.has(code)) {
              nextSet.add(code);
              changed = true;
            }
          });
          processedZones.push(zoneType);
        }
      });
      if (processedZones.length) {
        processedZones.forEach((zoneType) => pendingZones.delete(zoneType));
      }
      const next = Array.from(nextSet);
      if (!changed && next.length === prev.length) {
        return prev;
      }
      return next;
    });
  }, [censusZoneSelection, stateCensusOptions]);

  useEffect(() => {
    if (!isZoneSelectionActive) return;
    applyCensusCodesFromZones(selectedZoneTracts);
  }, [selectedZoneTracts, isZoneSelectionActive]);

  const useMyCurrentLocation = () => {
    if (!navigator.geolocation) {
      dispatch(showToast({ type: "error", message: "Geolocation is not supported by your browser." }));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        try {
          const res = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${GOOGLE_API_KEY}`
          );
          const data = await res.json();
          if (data.status === "OK" && data.results.length > 0) {
            const place = data.results[0];
            const { city, state, zip, streetAddress } = parseGooglePlace(place);
            setForm((f) => ({
              ...f,
              city,
              state: getStateCode(state),
              zip,
              streetAddress,
              autocompleteValue: `${city}, ${state} ${zip}`,
            }));
            setMapCenter({ lat: latitude, lng: longitude });
            setMapZoom(13);
            setMarker({ lat: latitude, lng: longitude });
          } else {
            dispatch(showToast({ type: "error", message: "Unable to retrieve location details." }));
          }
        } catch (error) {
          dispatch(showToast({ type: "error", message: "Failed to fetch location." }));
        }
      },
      () => {
        dispatch(showToast({ type: "error", message: "Permission denied for location access." }));
      }
    );
  };

  const handleCodeInputChange = (inputKey, arrayKey, value, options = {}) => {
    const { minLength = 6, maxLength = 6, label = "code" } = options;
    const pattern =
      minLength === maxLength
        ? new RegExp(`^\\d{${minLength}}$`)
        : new RegExp(`^\\d{${minLength},${maxLength}}$`);
    const parsed = parseCodeInput(value);
    const validList = parsed.filter((code) => pattern.test(code));
    const hasInvalid = parsed.length > 0 && validList.length !== parsed.length;
    const errorMessage =
      minLength === maxLength
        ? `Each ${label} must be a ${minLength}-digit number.`
        : `Each ${label} must be a ${minLength} to ${maxLength}-digit number.`;
    setForm((prev) => ({
      ...prev,
      [inputKey]: value,
      [arrayKey]: validList,
    }));
    setErrors((prev) => ({
      ...prev,
      [arrayKey]: hasInvalid ? errorMessage : "",
    }));
  };

  function validate() {
    let err = { ...errors, stateOrLocation: false, locationRequired: false };
    if (mode === LOCATION_MODE) {
      const hasLocationValue =
        typeof form.autocompleteValue === "string" &&
        form.autocompleteValue.trim().length > 0;
      if (!hasLocationValue) {
        err.locationRequired = true;
        setErrors(err);
        dispatch(
          showToast({
            type: "error",
            message: "Location is required for this search.",
          })
        );
        return false;
      }
    }
    if (mode === MANUAL_CALC_MODE && (!form.state.trim() || form.state.length !== 2)) {
      err.stateOrLocation = true;
      setErrors(err);
      dispatch(showToast({ type: "error", message: "State is required and must be a 2-letter code for manual calculation." }));
      return false;
    }
    if (mode !== LOCATION_MODE) {
      if (err.naicsCodes) {
        setErrors(err);
        return false;
      }
      if (err.censusCodes) {
        setErrors(err);
        return false;
      }
    }
    if (mode === MANUAL_CALC_MODE) {
      const requiredTextFields = [
        { key: "businessName", message: "Business name is required for manual calculation." },
        { key: "streetAddress", message: "Street address is required for manual calculation." },
        { key: "city", message: "City is required for manual calculation." },
        { key: "zip", message: "Zip code is required for manual calculation." },
        { key: "businessType", message: "Business type is required for manual calculation." },
      ];
      for (const field of requiredTextFields) {
        if (!String(form[field.key] || "").trim()) {
          setErrors(err);
          dispatch(showToast({ type: "error", message: field.message }));
          return false;
        }
      }
      const requiredNumericFields = [
        { key: "annualHires", message: "Please enter a valid annual hires value." },
      ];
      for (const field of requiredNumericFields) {
        const value = form[field.key];
        if (value === "" || value === null || typeof value === "undefined") {
          setErrors(err);
          dispatch(showToast({ type: "error", message: field.message }));
          return false;
        }
        const parsed = Number(value);
        if (Number.isNaN(parsed) || parsed < 0) {
          setErrors(err);
          dispatch(showToast({ type: "error", message: field.message }));
          return false;
        }
      }
    }
    // Validate zip format whenever a value is provided (5-digit or 9-digit)
    const zipValue = String(form.zip || "").trim();
    if (zipValue && !/^\d{5}(-\d{4})?$/.test(zipValue)) {
      setErrors(err);
      dispatch(showToast({ type: "error", message: "Zip code must be 5 digits (12345) or 9 digits (12345-6789)." }));
      return false;
    }
    setErrors(err);
    return true;
  }

  function buildParams() {
    const params = {
      BusinessName: form.businessName,
      StreetAddress: form.streetAddress,
      City: form.city,
      State: form.state,
      Zip: form.zip,
      BusinessNameSearchType: "StartsWith",
    };
    if (form.minEmployeeCount) params.minEmployeeCount = form.minEmployeeCount;
    if (form.maxEmployeeCount) params.maxEmployeeCount = form.maxEmployeeCount;
    if (form.businessType) params.BusinessType = form.businessType;
    if (Array.isArray(form.naicsCodes) && form.naicsCodes.length) {
      params.NAICS = form.naicsCodes;
    }
    if (Array.isArray(form.censusCodes) && form.censusCodes.length) {
      params.CensusCodes = form.censusCodes;
    }
    return params;
  }

  const extractBusinessArray = (response) => {
    if (!response) return [];
    if (Array.isArray(response.data)) return response.data;
    if (Array.isArray(response.data?.data)) return response.data.data;
    return [];
  };

  function buildManualSearchParams(includeBusinessType = false) {
    const params = {
      BusinessName: form.businessName,
      StreetAddress: form.streetAddress,
      City: form.city,
      State: form.state,
      Zip: form.zip,
      minEmployeeCount: form.minEmployeeCount,
      maxEmployeeCount: form.maxEmployeeCount,
    };
    if (includeBusinessType && form.businessType.trim()) {
      params.BusinessType = form.businessType.trim();
    }
    if (Array.isArray(form.naicsCodes) && form.naicsCodes.length) {
      params.NAICS = form.naicsCodes;
    }
    if (Array.isArray(form.censusCodes) && form.censusCodes.length) {
      params.CensusCodes = form.censusCodes;
    }
    Object.keys(params).forEach((key) => {
      if (params[key] === "" || params[key] === null || params[key] === undefined) {
        delete params[key];
      }
    });
    return params;
  }

  const buildFiltersSnapshot = (source = form) => ({
    ...source,
    naicsCodes: Array.isArray(source.naicsCodes) ? [...source.naicsCodes] : [],
    censusCodes: Array.isArray(source.censusCodes) ? [...source.censusCodes] : [],
  });

  const fetchRecordCount = async (params) => {
    const response = await axiosInstance.get(
      "/businessSearch/count",
      buildSearchRequestConfig(params)
    );
    return {
      recordCount: Number(response.data?.recordCount || 0),
      isProximityBasedCount: response.data?.isProximityBasedCount,
    };
  };

  const computeZoneAwareCount = async (baseParams, filtersSnapshot, zoneFilters) => {
    const initialResult = await fetchRecordCount(baseParams);
    if (!Array.isArray(zoneFilters) || zoneFilters.length === 0 || initialResult.recordCount > 0) {
      return {
        ...initialResult,
        filters: filtersSnapshot,
        enforcedZoneFilters: [],
        zoneFallbackApplied: false,
      };
    }
    const fallbackParams = { ...baseParams };
    delete fallbackParams.CensusCodes;
    const fallbackFilters = {
      ...filtersSnapshot,
      censusCodes: [],
      censusInput: "",
    };
    const fallbackResult = await fetchRecordCount(fallbackParams);
    if (fallbackResult.recordCount > 0) {
      return {
        ...fallbackResult,
        filters: fallbackFilters,
        enforcedZoneFilters: zoneFilters,
        zoneFallbackApplied: true,
      };
    }
    return {
      ...initialResult,
      filters: filtersSnapshot,
      enforcedZoneFilters: [],
      zoneFallbackApplied: false,
    };
  };

  const handleSearchError = (error, defaultMessage = "Search failed. Please check your details.") => {
    if (error?.response?.data?.errors?.State) {
      dispatch(
        showToast({
          type: "error",
          message: error.response.data.errors.State[0],
        })
      );
    } else if (error?.response?.data?.message) {
      dispatch(
        showToast({
          type: "error",
          message: error.response.data.message,
        })
      );
    } else {
      dispatch(
        showToast({
          type: "error",
          message: defaultMessage,
        })
      );
    }
  };

  const lookupBusinessCredits = async (business, annualHiresOverride) => {
    if (!business?.prospectBusinessId) return null;
    const hasAnnualHiresOverride =
      typeof annualHiresOverride === "number" && !Number.isNaN(annualHiresOverride);
    const payload = [
      {
        prospectBusinessId: business.prospectBusinessId,
        ...(hasAnnualHiresOverride ? { annualHiresOverride } : {}),
      },
    ];
    const lookupResponse = await axiosInstance.post("/taxCredits/lookup/v2", payload);
    const lookupData = extractBusinessArray(lookupResponse);
    const matched = lookupData.find(
      (item) => item.prospectBusinessId === business.prospectBusinessId
    );
    const taxCredits = matched
      ? [
          {
            totalProjectedTaxCreditAmount: matched.totalProjectedTaxCreditAmount,
            availableTaxCredits: matched.availableTaxCredits || [],
          },
        ]
      : [];
    const businessHires =
      hasAnnualHiresOverride && !Number.isNaN(annualHiresOverride)
        ? annualHiresOverride
        : Number(matched?.annualHires ?? matched?.AnnualHires ?? business?.annualHires ?? business?.AnnualHires);
    const finalAnnualHires =
      typeof businessHires === "number" && !Number.isNaN(businessHires) ? businessHires : undefined;
    const baseBusiness = {
      ...business,
      ...(matched || {}),
    };
    return {
      ...baseBusiness,
      annualHires: typeof finalAnnualHires === "number" ? finalAnnualHires : undefined,
      taxCredits,
    };
  };

  const handleManualCalcSearch = async () => {
    const params = buildManualSearchParams(true);
    const response = await axiosInstance.get(
      "/businessSearch/ManualBusinessSearch",
      buildSearchRequestConfig(params)
    );
    const responseData = extractBusinessArray(response);
    if (!responseData.length) {
      return [];
    }
    const business = responseData[0];
    const hiresValue = Number(form.annualHires);
    const businessWithCredits = await lookupBusinessCredits(business, hiresValue);
    return businessWithCredits ? [businessWithCredits] : [];
  };

  const handleManualEntrySearch = async (
    selectedZoneFilters,
    initialZoneFilter,
    searchSessionId
  ) => {
    const params = buildManualSearchParams(false);
    const filtersSnapshot = buildFiltersSnapshot(form);
    const countContext = await computeZoneAwareCount(params, filtersSnapshot, selectedZoneFilters);

    navigate(`${roleBasePath}/blogs?page=1`, {
      state: {
        filters: countContext.filters,
        pageSize: rolePageSize,
        recordCount: countContext.recordCount,
        isProximityBasedCount: countContext.isProximityBasedCount,
        isEaxctNameSearchSelected: false,
        searchMapCenter: mapCenter,
        initialZoneFilter,
        enforcedZoneFilters: countContext.enforcedZoneFilters,
        searchSessionId,
      },
    });
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setIsLoading(true);
    dispatch(clearFindBusinessResults());
    const searchSessionId = Date.now();
    const selectedZoneFilters = mapZoneSelectionToFilters(censusZoneSelection);
    const zoneFilterPreset =
      selectedZoneFilters.length === 1
        ? selectedZoneFilters[0]
        : selectedZoneFilters.length > 1
        ? EMPOWERMENT_FILTER_VALUES.ALL
        : undefined;

    try {
      if (mode === MANUAL_CALC_MODE) {
        const businesses = await handleManualCalcSearch();
        if (businesses.length > 0) {
          navigate(`${roleBasePath}/blogs?page=1`, {
            state: {
              companies: businesses,
              filters: { ...form },
              pageSize: rolePageSize,
              recordCount: businesses.length,
              isProximityBasedCount: false,
              isEaxctNameSearchSelected: false,
              searchMapCenter: mapCenter,
              isManualSearch: true,
              searchSessionId,
            },
          });
        } else {
          dispatch(
            showToast({
              type: "error",
              message: "No businesses found with the provided details.",
            })
          );
        }
        return;
      }

      if (mode === MANUAL_ENTRY_MODE) {
        await handleManualEntrySearch(
          selectedZoneFilters,
          zoneFilterPreset,
          searchSessionId
        );
        return;
      }

      const params = buildParams();
      const filtersSnapshot = buildFiltersSnapshot(form);
      const countContext = await computeZoneAwareCount(
        params,
        filtersSnapshot,
        selectedZoneFilters
      );
      navigate(`${roleBasePath}/blogs?page=1`, {
        state: {
          filters: countContext.filters,
          pageSize: rolePageSize,
          recordCount: countContext.recordCount,
          isProximityBasedCount: countContext.isProximityBasedCount,
          isEaxctNameSearchSelected: false,
          searchMapCenter: mapCenter,
          initialZoneFilter: zoneFilterPreset,
          enforcedZoneFilters: countContext.enforcedZoneFilters,
          searchSessionId,
        },
      });
    } catch (error) {
      handleSearchError(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="">
      <div className="find-business-content">
        <div className="find-business-map">
          <div className="find-business-card">
            <form className="exact-card" autoComplete="off" onSubmit={handleSearch}>
              <div className="exact-title">Search For Businesses</div>
              <div className="exact-subtext">Check if your business qualifies for tax credits</div>
              <div className="exact-label">
                Business Name {mode === MANUAL_CALC_MODE && <span className="asterisk">*</span>}
              </div>
              <input
                className="exact-input"
                type="text"
                placeholder="Enter business name"
                value={form.businessName}
                required={mode === MANUAL_CALC_MODE}
                onChange={(e) => setForm((f) => ({ ...f, businessName: e.target.value }))}
              />

              {mode === LOCATION_MODE ? (
                <>
                  <div className="exact-label location-label-row">
                    <span>
                      Location <span className="asterisk">*</span>
                    </span>
                    <span className="location-action-link" onClick={useMyCurrentLocation}>
                      Use my current location
                    </span>
                  </div>
                  <AutocompleteCustom
                    onPlaceSelect={setSelectedPlace}
                    value={form.autocompleteValue}
                    onChange={(e) => setForm((f) => ({ ...f, autocompleteValue: e.target.value }))}
                  />
                  {errors.locationRequired && (
                    <div className="exact-error">Location is required.</div>
                  )}
                  <div
                    className="exact-link advance-search-link"
                    onClick={() => setMode(MANUAL_ENTRY_MODE)}
                  >
                    Advanced Search
                  </div>
                  <button
                    type="submit"
                    className="exact-btn"
                    disabled={isLoading || (mode === MANUAL_CALC_MODE && !manualCalcReady)}
                  >
                    {isLoading ? "Searching..." : "Search for Tax Credits"}
                  </button>
                  <div className="or-divider">
                    <span>OR</span>
                  </div>
                  <div>
                    <button
                      type="button"
                      className="exact-btn"
                      onClick={() => setMode(MANUAL_CALC_MODE)}
                      style={{
                        backgroundColor: "#fff",
                        color: "#23244c",
                        border: "1px solid #D5D8DC",
                        boxShadow: "none",
                      }}
                    >
                      Calculate Tax Credits Manually
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="exact-label">
                    Street Address {mode === MANUAL_CALC_MODE && <span className="asterisk">*</span>}
                  </div>
                  <input
                    className="exact-input"
                    type="text"
                    placeholder="e.g. 123 Apple Street"
                    value={form.streetAddress}
                    required={mode === MANUAL_CALC_MODE}
                    onChange={(e) => setForm((f) => ({ ...f, streetAddress: e.target.value }))}
                  />
                  <div className="exact-label">
                    Zip Code {mode === MANUAL_CALC_MODE && <span className="asterisk">*</span>}
                  </div>
                  <input
                    className="exact-input"
                    type="text"
                    placeholder="12345 or 12345-6789"
                    value={form.zip}
                    required={mode === MANUAL_CALC_MODE}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, "").slice(0, 9);
                      const formatted = digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
                      setForm((f) => ({ ...f, zip: formatted }));
                    }}
                  />
                  <div className="exact-label">
                    City {mode === MANUAL_CALC_MODE && <span className="asterisk">*</span>}
                  </div>
                  <input
                    className="exact-input"
                    type="text"
                    placeholder="e.g. New York"
                    value={form.city}
                    required={mode === MANUAL_CALC_MODE}
                    onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
                  />
                  <div className="exact-label">
                    State {mode === MANUAL_CALC_MODE && <span className="asterisk">*</span>}
                  </div>
                  <select
                    className={`exact-input${
                      mode === MANUAL_CALC_MODE && errors.stateOrLocation ? " error" : ""
                    }`}
                    value={form.state}
                    required={mode === MANUAL_CALC_MODE}
                    onChange={(e) => setForm((f) => ({ ...f, state: e.target.value }))}
                  >
                    <option value="">Select state</option>
                    {US_STATES.map((st) => (
                      <option key={st.code} value={st.code}>{st.name}</option>
                    ))}
                  </select>
                  {mode !== MANUAL_CALC_MODE && (
                    <>
                      <div className="exact-label">Minimum Employee Count (Optional)</div>
                      <input
                        className="exact-input"
                        type="number"
                        placeholder="e.g. 10"
                        min="0"
                        value={form.minEmployeeCount}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, minEmployeeCount: e.target.value }))
                        }
                      />
                      <div className="exact-label">Maximum Employee Count (Optional)</div>
                      <input
                        className="exact-input"
                        type="number"
                        placeholder="e.g. 100"
                        min="0"
                        value={form.maxEmployeeCount}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, maxEmployeeCount: e.target.value }))
                        }
                      />
                    </>
                  )}
                  {mode === MANUAL_ENTRY_MODE && (
                    <>
                      <div className="exact-label">Business Type (Optional)</div>
                      <div className="naics-codes-wrapper" ref={naicsCodesDropdownRef}>
                        <textarea
                          className="exact-input"
                          rows={2}
                          placeholder="Enter Multiple Business Types or NAICS Codes separated by Comma"
                          value={form.naicsInput}
                          onFocus={handleNaicsCodesFocus}
                          onChange={(e) => handleNaicsCodesChange(e.target.value)}
                        />
                        {isNaicsCodesDropdownOpen && (
                          <div className="naics-dropdown naics-codes-dropdown">
                            {isNaicsLoading ? (
                              <div className="naics-dropdown-empty">
                                Loading NAICS codes...
                              </div>
                            ) : !hasFetchedNaicsRef.current &&
                              !hasValidSearchTerm(naicsCodeSearchTerm) ? (
                              <div className="naics-dropdown-empty">
                                Enter at least 2 characters to search NAICS codes
                                or business types.
                              </div>
                            ) : filteredNaicsCodeOptions.length ? (
                              filteredNaicsCodeOptions.map((option) => (
                                <div
                                  key={`${option.naicsCode || "code"}-${
                                    option.naicsDescription || "desc"
                                  }-picker`}
                                  className="naics-dropdown-row"
                                  onMouseDown={(event) => {
                                    event.preventDefault();
                                    handleNaicsCodeOptionSelect(option);
                                  }}
                                >
                                  <div className="naics-dropdown-description">
                                    {option.naicsDescription || "No description"}
                                  </div>
                                  <div className="naics-dropdown-code">
                                    {option.naicsCode || ""}
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div className="naics-dropdown-empty">
                                No NAICS codes match your search.
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      {errors.naicsCodes && (
                        <div className="exact-error">{errors.naicsCodes}</div>
                      )}
                      <div className="exact-label">Census Tracts (Optional)</div>
                      <div className="census-zone-checkboxes">
                        <label
                          className={`census-zone-option${
                            !isStateReady ? " disabled" : ""
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={censusZoneSelection.fez}
                            onChange={() => toggleZoneSelection("fez")}
                            disabled={!isStateReady}
                          />
                          FEZ
                        </label>
                        <label
                          className={`census-zone-option${
                            !isStateReady ? " disabled" : ""
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={censusZoneSelection.rrc}
                            onChange={() => toggleZoneSelection("rrc")}
                            disabled={!isStateReady}
                          />
                          RRC
                        </label>
                        {isZoneLoading && (
                          <span className="census-zone-status">Loading...</span>
                        )}
                      </div>
                      {isZoneSelectionActive && (
                        <div className="census-dropdown-panel">
                          <div className="census-dropdown-header">
                            <div className="census-dropdown-title">
                              Select Census Tracts ({selectedZoneTracts.length} selected)
                            </div>
                            <div className="census-dropdown-actions">
                              <button
                                type="button"
                                className="census-dropdown-action-btn"
                                disabled={!zoneCensusOptions.length}
                                onClick={() =>
                                  setIsCensusDropdownOpen((open) => !open)
                                }
                              >
                                {isCensusDropdownOpen ? "Hide list" : "Show list"}
                              </button>
                              <button
                                type="button"
                                className="census-dropdown-action-btn"
                                disabled={!zoneCensusOptions.length}
                                onClick={selectAllCensusOptions}
                              >
                                Select all
                              </button>
                              <button
                                type="button"
                                className="census-dropdown-action-btn"
                                disabled={!selectedZoneTracts.length}
                                onClick={clearSelectedCensusOptions}
                              >
                                Clear
                              </button>
                            </div>
                          </div>
                          {isCensusDropdownOpen && (
                            <div className="census-dropdown">
                              {zoneCensusOptions.length ? (
                                zoneCensusOptions.map((option) => {
                                  const isChecked = selectedZoneTractSet.has(
                                    option.code
                                  );
                                  const zoneLabel =
                                    option.zoneType ===
                                    EMPOWERMENT_ZONE_TYPES.EMPOWERMENT
                                      ? "FEZ"
                                      : "RRC";
                                  return (
                                  <label
                                    key={option.code}
                                    className={`census-dropdown-option${
                                      isChecked ? " selected" : ""
                                    }`}
                                  >
                                    <div className="census-dropdown-option-content">
                                      <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={() =>
                                          toggleCensusOptionSelection(option.code)
                                        }
                                      />
                                      <div className="census-dropdown-option-text">
                                        <div className="census-dropdown-code-row">
                                          <span className="census-dropdown-code">
                                            {option.code}
                                          </span>
                                          { (option.name || option.meta) && (
                                            <span className="census-dropdown-name">
                                              {option.name || option.meta}
                                            </span>
                                          )}
                                          <span
                                            className={`census-zone-chip ${option.zoneType}`}
                                          >
                                            {zoneLabel}
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  </label>
                                  );
                                })
                              ) : (
                                <div className="census-dropdown-empty">
                                  {zoneError ||
                                    "No census tracts found for the selected option(s) in this state."}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                      {zoneError && (
                        <div className="exact-error">{zoneError}</div>
                      )}
                      <div
                        className={`census-manual-input-wrapper${
                          isZoneSelectionActive ? " disabled" : ""
                        }`}
                        ref={manualCensusDropdownRef}
                      >
                        <textarea
                          className={`exact-input${
                            isZoneSelectionActive ? " readonly" : ""
                          }`}
                          rows={2}
                          placeholder="Enter multiple 6-digit codes separated by commas, or search by tract or county."
                          value={form.censusInput}
                          readOnly={isZoneSelectionActive}
                          aria-readonly={isZoneSelectionActive}
                          onFocus={handleManualCensusFocus}
                          onChange={(e) => {
                            if (isZoneSelectionActive) return;
                            handleManualCensusTextChange(e.target.value);
                          }}
                        />
                        {!isZoneSelectionActive &&
                          isManualCensusDropdownOpen && (
                            <div className="naics-dropdown census-manual-dropdown">
                              {stateCensusOptions.length === 0 ? (
                                <div className="naics-dropdown-empty">
                                  Select a state to load census tracts.
                                </div>
                              ) : manualCensusSuggestions.length ? (
                                manualCensusSuggestions.map((option) => (
                                  <div
                                    key={`${option.code}-${
                                      option.name || "name"
                                    }`}
                                    className="naics-dropdown-row"
                                    onMouseDown={(event) => {
                                      event.preventDefault();
                                      handleManualCensusOptionSelect(option);
                                    }}
                                  >
                                    <div className="census-dropdown-code-row">
                                      <span className="census-dropdown-code">
                                        {option.code}
                                      </span>
                                      {option.name && (
                                        <span className="census-dropdown-name">
                                          {option.name}
                                        </span>
                                      )}
                                      <span
                                        className={`census-zone-chip ${option.zoneType}`}
                                      >
                                        {option.zoneType ===
                                        EMPOWERMENT_ZONE_TYPES.EMPOWERMENT
                                          ? "FEZ"
                                          : "RRC"}
                                      </span>
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <div className="naics-dropdown-empty">
                                  No census tracts match your search.
                                </div>
                              )}
                            </div>
                          )}
                      </div>
                      {isZoneSelectionActive && (
                        <div className="exact-helper-text">
                          Selected census tracts will appear above. Uncheck FEZ/RRC to type codes manually.
                        </div>
                      )}
                      {errors.censusCodes && (
                        <div className="exact-error">{errors.censusCodes}</div>
                      )}
                    </>
                  )}
                  {mode === MANUAL_CALC_MODE && (
                    <>
                      <div className="exact-label">
                        Business Type <span className="asterisk">*</span>
                      </div>
                      <div className="naics-codes-wrapper" ref={businessTypeDropdownRef}>
                        <textarea
                          className="exact-input"
                          rows={2}
                          placeholder="Enter Multiple Business Types or NAICS Codes separated by Comma"
                          required
                          value={form.businessType}
                          onFocus={handleBusinessTypeFocus}
                          onChange={(e) => handleBusinessTypeInputChange(e.target.value)}
                        />
                        {isBusinessTypeDropdownOpen && (
                          <div className="naics-dropdown">
                            {isNaicsLoading ? (
                              <div className="naics-dropdown-empty">
                                Loading NAICS codes...
                              </div>
                            ) : filteredNaicsOptions.length ? (
                              filteredNaicsOptions.map((option) => (
                                <div
                                  key={`${option.naicsCode || "code"}-${
                                    option.naicsDescription || "desc"
                                  }`}
                                  className="naics-dropdown-row"
                                  onMouseDown={(event) => {
                                    event.preventDefault();
                                    handleBusinessTypeOptionSelect(option);
                                  }}
                                >
                                  <div className="naics-dropdown-description">
                                    {option.naicsDescription || "No description"}
                                  </div>
                                  <div className="naics-dropdown-code">
                                    {option.naicsCode || ""}
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div className="naics-dropdown-empty">
                                No NAICS codes match your search.
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="exact-label">
                        Annual Hires <span className="asterisk">*</span>
                      </div>
                      <input
                        className="exact-input"
                        type="number"
                        placeholder="Annual hires"
                        min="0"
                        required
                        value={form.annualHires}
                        onChange={(e) => setForm((f) => ({ ...f, annualHires: e.target.value }))}
                      />
                    </>
                  )}
                  {mode === MANUAL_CALC_MODE && errors.stateOrLocation && (
                    <div className="exact-error">
                      State is required and must be a 2-letter code
                    </div>
                  )}
                  <button
                    type="submit"
                    className="exact-btn"
                    disabled={isLoading || (mode === MANUAL_CALC_MODE && !manualCalcReady)}
                  >
                    {isLoading ? "Searching..." : "Search for Tax Credits"}
                  </button>
                  <div className="exact-link" onClick={() => setMode(LOCATION_MODE)}>
                    Use my current location instead
                  </div>
                </>
              )}
            </form>
          </div>
          <APIProvider apiKey={GOOGLE_API_KEY} libraries={["places"]}>
            <GoogleMap
              key={mapKey}
              mapId="fd16af6e873de8bf6283afde"
              style={{
                width: "100%",
                height: "100%",
                minHeight: "450px",
                borderRadius: 18,
                background: "#eee",
                position: "relative",
              }}
              zoom={mapZoom}
              center={mapCenter}
              gestureHandling={"greedy"}
              disableDefaultUI={false}
              options={{
                mapTypeControl: false,
                streetViewControl: false,
                fullscreenControl: true,
              }}
            >
              <AutocompleteResult place={selectedPlace} />
            </GoogleMap>
          </APIProvider>
        </div>
      </div>
    </div>
  );
}


export default ReviewDetails;
